import Axios from 'axios';
import * as React from 'react';
import { useEffect } from 'react';
import {
  useRefreshToken,
  useSetToken,
  useSetRefreshToken,
  useSetUser,
  useToken,
} from 'utils/contexts/app-context';

export const SERVER_URL: string = `${
  process.env.REACT_APP_NODE_SERVER_URL || ''
}/app`;

const instance = Axios.create({
  baseURL: `${SERVER_URL}/`,
});

let refreshingCall: Promise<string> | null = null;

const useAPI = () => {
  // Do not use the token and user when doing 'auth' requests (like login)
  const token = useToken();
  const refreshToken = useRefreshToken();
  const setToken = useSetToken();
  const setRefreshToken = useSetRefreshToken();
  const setUser = useSetUser();

  // Function that will be called to refresh authorization
  const refreshAuthLogic = React.useCallback(async () => {
    if (refreshingCall) {
      return refreshingCall;
    }

    refreshingCall = (async () => {
      const { data } = await Axios.post(`${SERVER_URL}/auth/refresh`, {
        refresh: refreshToken,
      });
      return data.access;
    })();

    const access = await refreshingCall;

    setToken(access);
    refreshingCall = null;

    return access;
  }, [refreshToken, setToken]);

  useEffect(() => {
    const interceptor = instance.interceptors.response.use(
      response => response,
      async error => {
        const status = error.response ? error.response.status : null;

        if (status === 401) {
          const newToken = await refreshAuthLogic();
          error.config.headers.Authorization = `Bearer ${newToken}`;
          error.config.baseURL = undefined;
          try {
            return await Axios.request(error.config);
          } catch (responseError: any) {
            if (
              responseError.response &&
              responseError.response.status === 401
            ) {
              setToken(null);
              setRefreshToken(null);
              setUser(null);
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      instance.interceptors.response.eject(interceptor);
    };
  }, [refreshAuthLogic, setToken, setRefreshToken, setUser]);

  React.useEffect(() => {
    instance.defaults.headers.common.Authorization =
      token == null ? '' : `Bearer ${token}`;
  }, [token]);

  // PDF Settings API helpers
  const getPDFSettings = async () => {
    const { data } = await instance.get('/userPdfSettings/pdf-settings');
    return data.settings;
  };

  const savePDFSettings = async (settings: any) => {
    const { data } = await instance.post('/userPdfSettings/pdf-settings', { settings });
    return data.settings;
  };

  // MFA API helpers
  const getMfaPreference = async () => {
    const { data } = await instance.get('/auth/mfa/preference');
    return data;
  };

  const setupTotp = async (email?: string) => {
    // If email is provided, use login-time endpoint (no auth required)
    if (email) {
      const { data } = await instance.post('/auth/login/setup/totp', { email });
      return data;
    }
    // Otherwise use authenticated endpoint
    const { data } = await instance.post('/auth/mfa/setup/totp');
    return data;
  };

  const verifyTotp = async (code: string, email?: string) => {
    // If email is provided, use login-time endpoint (no auth required)
    if (email) {
      const { data } = await instance.post('/auth/login/verify/totp', { email, code });
      return data;
    }
    // Otherwise use authenticated endpoint
    const { data } = await instance.post('/auth/mfa/verify/totp', { code });
    return data;
  };

  const setupSms = async (phoneNumber: string, email?: string) => {
    // If email is provided, use login-time endpoint (no auth required)
    if (email) {
      const { data } = await instance.post('/auth/login/setup/sms', { email, phoneNumber });
      return data;
    }
    // Otherwise use authenticated endpoint
    const { data } = await instance.post('/auth/mfa/setup/sms', { phoneNumber });
    return data;
  };

  const verifySms = async (code: string, email?: string) => {
    // If email is provided, use login-time endpoint (no auth required)
    if (email) {
      const { data } = await instance.post('/auth/login/verify/sms', { email, code });
      return data;
    }
    // Otherwise use authenticated endpoint
    const { data } = await instance.post('/auth/mfa/verify/sms', { code });
    return data;
  };

  const switchMfaMethod = async (preferredMethod: 'TOTP' | 'SMS') => {
    const { data } = await instance.post('/auth/mfa/switch', { preferredMethod });
    return data;
  };

  const disableMfa = async () => {
    const { data } = await instance.post('/auth/mfa/disable');
    return data;
  };

  const disableMfaMethod = async (method: 'TOTP' | 'SMS') => {
    const { data } = await instance.post('/auth/mfa/disable-method', { method });
    return data;
  };

  return Object.assign(instance, {
    getPDFSettings,
    savePDFSettings,
    getMfaPreference,
    setupTotp,
    verifyTotp,
    setupSms,
    verifySms,
    switchMfaMethod,
    disableMfa,
    disableMfaMethod,
  });
};

export default useAPI;
